/**
 * Context Switcher Component
 *
 * Allows switching between different application contexts:
 * - App: Main application view
 * - Admin: Administrative interface
 * - Portal: Client/customer portal view
 */

import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { portalAPI } from '../services/api';
import { eventBus, Events } from '../services/events';

type ContextType = 'app' | 'admin' | 'portal';

interface ContextConfig {
  type: ContextType;
  label: string;
  icon: string;
  url?: string;
}

@customElement('moya-context-switcher')
export class MoyaContextSwitcher extends LitElement {
  static styles = css`
    :host {
      display: inline-flex;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    }

    .switcher {
      display: inline-flex;
      background: rgba(255, 255, 255, 0.05);
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 8px;
      padding: 2px;
      gap: 2px;
    }

    .context-btn {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 6px 12px;
      border: none;
      background: transparent;
      border-radius: 6px;
      cursor: pointer;
      color: rgba(255, 255, 255, 0.6);
      font-size: 12px;
      font-weight: 500;
      transition: all 0.15s ease;
      white-space: nowrap;
    }

    .context-btn:hover {
      color: rgba(255, 255, 255, 0.9);
      background: rgba(255, 255, 255, 0.05);
    }

    .context-btn:focus {
      outline: none;
      box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.3);
    }

    .context-btn.active {
      background: rgba(59, 130, 246, 0.2);
      color: #60a5fa;
    }

    .context-btn.active:hover {
      background: rgba(59, 130, 246, 0.3);
    }

    .context-icon {
      width: 14px;
      height: 14px;
    }

    .context-label {
      display: none;
    }

    @media (min-width: 768px) {
      .context-label {
        display: inline;
      }
    }

    /* Tooltip for mobile */
    .context-btn {
      position: relative;
    }

    @media (max-width: 767px) {
      .context-btn::after {
        content: attr(data-label);
        position: absolute;
        bottom: -24px;
        left: 50%;
        transform: translateX(-50%);
        padding: 4px 8px;
        background: rgba(0, 0, 0, 0.9);
        color: white;
        font-size: 11px;
        border-radius: 4px;
        white-space: nowrap;
        opacity: 0;
        pointer-events: none;
        transition: opacity 0.15s ease;
      }

      .context-btn:hover::after {
        opacity: 1;
      }
    }
  `;

  @property({ type: String }) currentContext: ContextType = 'app';
  @property({ type: Array }) availableContexts: ContextConfig[] = [
    { type: 'app', label: 'App', icon: 'app' },
    { type: 'admin', label: 'Admin', icon: 'admin' },
    { type: 'portal', label: 'Portal', icon: 'portal' },
  ];
  @property({ type: Boolean }) showLabels = true;

  @state() private loading = false;

  private async handleContextSwitch(context: ContextConfig) {
    if (context.type === this.currentContext || this.loading) {
      return;
    }

    this.loading = true;

    try {
      // Notify server of context switch
      await portalAPI.switchContext(context.type);

      // Dispatch custom event
      this.dispatchEvent(
        new CustomEvent('moya-context-switch', {
          bubbles: true,
          composed: true,
          detail: { context: context.type },
        })
      );

      eventBus.emit(Events.CONTEXT_SWITCHED, context.type);

      // Navigate to context URL if provided
      if (context.url) {
        window.location.href = context.url;
      } else {
        this.currentContext = context.type;
      }
    } catch (e) {
      console.error('[ContextSwitcher] Failed to switch context:', e);
    } finally {
      this.loading = false;
    }
  }

  private renderIcon(iconType: string) {
    switch (iconType) {
      case 'app':
        return html`
          <svg class="context-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
            <line x1="3" y1="9" x2="21" y2="9" />
            <line x1="9" y1="21" x2="9" y2="9" />
          </svg>
        `;
      case 'admin':
        return html`
          <svg class="context-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          </svg>
        `;
      case 'portal':
        return html`
          <svg class="context-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
        `;
      default:
        return html`
          <svg class="context-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10" />
          </svg>
        `;
    }
  }

  render() {
    return html`
      <div class="switcher" role="tablist">
        ${this.availableContexts.map((context) => {
          const btnClasses = classMap({
            'context-btn': true,
            active: context.type === this.currentContext,
          });

          return html`
            <button
              class="${btnClasses}"
              @click="${() => this.handleContextSwitch(context)}"
              role="tab"
              aria-selected="${context.type === this.currentContext}"
              data-label="${context.label}"
              ?disabled="${this.loading}"
            >
              ${this.renderIcon(context.icon)}
              ${this.showLabels ? html`<span class="context-label">${context.label}</span>` : ''}
            </button>
          `;
        })}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'moya-context-switcher': MoyaContextSwitcher;
  }
}
